'use client'
import { SectionContent } from "@/types/sections.model";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import FadeIn from "../ui/FadeIn";
import Image from "next/image";

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import CustomIconButton from "../ui/CustomIconButton";
import { useWindowSize } from "@/hook/useWindowSize";
import type { QuoteItem } from "@/types/sections.model";
import { useRef } from "react";

const QuoteItem: React.FC<{ item: QuoteItem }> = ({ item }) => {
  return (
    <Paper
      square
      elevation={0}
      sx={{
        py: 48,
        px: { xs: 24, lg: 48 },
        bgcolor: theme => theme.vars.palette.grey[50],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
      }}
    >
      <Typography variant="headlineLGItalic" component={"p"} sx={{ mb: 32, }}>
        “{item.text}“
      </Typography>

      {item.author?.image ? (
        <Image
          src={item.author.image}
          alt={item.author.name}
          width={157}
          height={40}
        />
      ) : (
        <Typography component={"p"}>
          {item.author.name}
        </Typography>
      )}
      
    </Paper>
  )
}

const Quote: React.FC<{ section: SectionContent<'quote'> }> = ({ section }) => {
  const ref = useRef(null);
  const { id, items } = section;

  const { isMobile } = useWindowSize();

  return (
    <Box id={id} sx={{ py: 48, }}>
      <Container>

        <Box sx={{
          '& .swiper': {
            minHeight: '400px'
          },
          '& .swiper-slide': {
            height: 'auto',
            display: 'flex !important'
          }
        }}>
          <Swiper
            ref={ref}
            className={id}
            modules={[Navigation, Pagination]}
            spaceBetween={50}
            slidesPerView={1}
            autoHeight={false}
            navigation={{
              nextEl: '.custom-next',
              prevEl: '.custom-prev',
            }}
            pagination={{
              clickable: true,
              el: '.quote-dots',
            }}
          >
          {items.map((item, index) => (
            <SwiperSlide key={`${item.author.name}-${index}`} style={{ height: 'auto', display: 'flex', boxSizing: 'border-box' }}>
              <QuoteItem item={item} />
            </SwiperSlide>
          ))}
          </Swiper>
        </Box>

        {items?.length > 1 && (
          <FadeIn>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} mt={24}>
              <Box className="quote-dots" sx={{
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start',
                },
                display: 'flex',
                gap: 1,
                '& .swiper-pagination-bullet': {
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme => theme.palette.primary.main,
                  opacity: 0.25,
                  transition: 'all 0.3s ease',
                },
                '& .swiper-pagination-bullet-active': {
                  opacity: 1,
                },
              }} />


              {!isMobile && (
                <Stack direction={"row"} spacing={8}>

                  <CustomIconButton className="custom-prev" aria-label="Citazione precedente">
                    <KeyboardArrowLeftIcon />
                  </CustomIconButton>

                  <CustomIconButton className="custom-next" aria-label="Citazione successiva">
                    <KeyboardArrowRightIcon />
                  </CustomIconButton>

                </Stack>
              )}
            </Stack>
          </FadeIn>
        )}

      </Container>
    </Box>
  )
}

export default Quote;